import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Grid, 
  FlexContainer
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ReportType } from '../../types';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ReportCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow-lg);
  }
`;

const ReportCardHeader = styled(CardHeader)`
  background-color: var(--secondary-color);
  color: white;
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  
  h3 {
    color: white;
    margin: 0;
  }
`;

const ReportCardBody = styled(CardBody)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ReportDescription = styled.p`
  color: var(--dark-gray);
  margin-bottom: 1rem;
  flex: 1;
`;

const ReportFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  text-align: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #f1f3f4;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #F68B21;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #141340;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
`;

const RecentReports = styled.div`
  margin-bottom: 2rem;
`;

const RecentReportItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const ReportInfo = styled.div`
  flex: 1;
  
  .report-name {
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.25rem;
  }
  
  .report-meta {
    font-size: 0.85rem;
    color: #6c757d;
  }
`;

const ReportsList: React.FC = () => {
  const { user } = useAuth();
  const [reportsData, setReportsData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load reports data from API
  useEffect(() => {
    const loadReportsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load reports statistics from real API
        const response = await apiService.getReportsStats();
        setReportsData(response.data || {});
      } catch (error: any) {
        console.error('Error loading reports data:', error);
        setError('Failed to load reports data');
        setReportsData({});
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadReportsData();
    }
  }, [user]);

  // Define available report types based on user role
  const getAvailableReports = (): Array<{
    type: ReportType;
    title: string;
    description: string;
    icon: string;
    roles: string[];
    sampleData?: {
      totalReports: number;
      lastGenerated: string;
      avgGenerationTime: string;
    };
  }> => {
    return [
      {
        type: 'Project Reports',
        title: 'Project Reports',
        description: 'Detailed reports for individual projects including budget vs. spending, fund requests, and procurement.',
        icon: '📊',
        roles: ['MD', 'TD', 'Project Manager'],
        sampleData: {
          totalReports: 156,
          lastGenerated: '2024-01-15',
          avgGenerationTime: '2.3 seconds'
        }
      },
      {
        type: 'Consolidated Project Reports',
        title: 'Consolidated Project Reports',
        description: 'Aggregated reports across multiple projects for comparative analysis.',
        icon: '📈',
        roles: ['MD', 'TD', 'Project Manager'],
        sampleData: {
          totalReports: 42,
          lastGenerated: '2024-01-14',
          avgGenerationTime: '4.1 seconds'
        }
      },
      {
        type: 'Department Report',
        title: 'Department Reports',
        description: 'Reports focused on department-specific metrics and performance.',
        icon: '🏢',
        roles: ['MD', 'TD', 'HR (Admin)'],
        sampleData: {
          totalReports: 28,
          lastGenerated: '2024-01-13',
          avgGenerationTime: '1.8 seconds'
        }
      },
      {
        type: 'Overall Company Report',
        title: 'Company Overview',
        description: 'Comprehensive reports covering all aspects of company operations and finances.',
        icon: '🏭',
        roles: ['MD', 'TD'],
        sampleData: {
          totalReports: 15,
          lastGenerated: '2024-01-12',
          avgGenerationTime: '6.2 seconds'
        }
      },
      {
        type: 'Financial Report',
        title: 'Financial Reports',
        description: 'Detailed financial statements, cash flow analysis, and budget tracking.',
        icon: '💰',
        roles: ['MD', 'TD', 'Accountant'],
        sampleData: {
          totalReports: 89,
          lastGenerated: '2024-01-15',
          avgGenerationTime: '3.7 seconds'
        }
      },
      {
        type: 'Procurement Report',
        title: 'Procurement Reports',
        description: 'Reports on procurement activities, supplier performance, and spending analysis.',
        icon: '🛒',
        roles: ['MD', 'TD', 'Store-Keeper (Logistics)', 'Procurement', 'Project Manager'],
        sampleData: {
          totalReports: 67,
          lastGenerated: '2024-01-14',
          avgGenerationTime: '2.9 seconds'
        }
      },
      {
        type: 'Fund Request Report',
        title: 'Fund Request Reports',
        description: 'Analysis of fund requests, approval timelines, and allocation by project or category.',
        icon: '💵',
        roles: ['MD', 'TD', 'Accountant', 'Project Manager'],
        sampleData: {
          totalReports: 124,
          lastGenerated: '2024-01-15',
          avgGenerationTime: '2.1 seconds'
        }
      }
    ];
  };
  
  // Filter reports based on user role
  const availableReports = getAvailableReports().filter(report => {
    if (!user) return false;
    return report.roles.some(role => user.role.includes(role));
  });
  
  // Calculate total reports generated from real API data
  const totalReportsGenerated = Object.values(reportsData).reduce((sum: number, reportStats: any) => {
    return sum + (reportStats?.totalReports || 0);
  }, 0);
  
  // Calculate average generation time from real data
  const avgGenerationTime = Object.values(reportsData).reduce((sum: number, reportStats: any) => {
    const timeStr = reportStats?.avgGenerationTime || '0 seconds';
    const timeInSeconds = parseFloat(timeStr.replace(' seconds', ''));
    return sum + timeInSeconds;
  }, 0) / Math.max(1, Object.keys(reportsData).length);
  
  // Get the most recent report generation date
  const lastGeneratedDates = Object.values(reportsData).map((reportStats: any) => 
    reportStats?.lastGenerated ? new Date(reportStats.lastGenerated) : null
  ).filter(date => date !== null);
  
  const mostRecentDate = lastGeneratedDates.length > 0 
    ? new Date(Math.max(...lastGeneratedDates.map(d => d!.getTime())))
    : null;
  
  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Reports & Analytics</h1>
        <FlexContainer gap="0.5rem">
          <OutlineButton>
            📋 Recent Reports
          </OutlineButton>
          <OutlineButton>
            💾 Saved Reports
          </OutlineButton>
          <Button>
            📊 Dashboard
          </Button>
        </FlexContainer>
      </PageHeader>
      
      <StatsContainer>
        <StatCard>
          <StatIcon>📊</StatIcon>
          <StatValue>{isLoading ? '...' : totalReportsGenerated}</StatValue>
          <StatLabel>Total Reports Generated</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>📈</StatIcon>
          <StatValue>{availableReports.length}</StatValue>
          <StatLabel>Report Types Available</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>⚡</StatIcon>
          <StatValue>{isLoading ? '...' : `${avgGenerationTime.toFixed(1)}s`}</StatValue>
          <StatLabel>Average Generation Time</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>📅</StatIcon>
          <StatValue>
            {isLoading ? '...' : mostRecentDate ? 
              mostRecentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 
              'N/A'
            }
          </StatValue>
          <StatLabel>Last Report Generated</StatLabel>
        </StatCard>
      </StatsContainer>
      
      <Card>
        <CardHeader>
          <h3>Report Generation</h3>
        </CardHeader>
        <CardBody>
          <p style={{ color: '#6c757d', textAlign: 'center', padding: '2rem' }}>
            Select a report type from below to generate comprehensive reports with real-time data analysis. 
            All reports use live data from your database and can be exported to Excel, PDF, or printed directly.
          </p>
        </CardBody>
      </Card>
      
      <Card>
        <CardHeader>
          <h3>Available Report Types</h3>
        </CardHeader>
        <CardBody>
          <p style={{ marginBottom: '2rem', color: '#6c757d' }}>
            Generate comprehensive reports with real-time data analysis and professional formatting. 
            Export to Excel, PDF, or print directly from the system.
          </p>
          
          <Grid columns="repeat(auto-fill, minmax(320px, 1fr))" gap="1.5rem">
            {availableReports.map((report, index) => {
              // Get real data for this report type
              const reportStats = reportsData[report.type] || {};
              
              return (
                <ReportCard key={index}>
                  <ReportCardHeader>
                    <h3>{report.icon} {report.title}</h3>
                  </ReportCardHeader>
                  <ReportCardBody>
                    <ReportDescription>{report.description}</ReportDescription>
                    
                    {!isLoading && (
                      <div style={{ 
                        background: '#f8f9fa', 
                        padding: '1rem', 
                        borderRadius: '8px', 
                        marginBottom: '1rem',
                        fontSize: '0.85rem'
                      }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>📊 {reportStats.totalReports || 0}</strong> reports generated
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>📅 Last:</strong> {reportStats.lastGenerated || 'N/A'}
                        </div>
                        <div>
                          <strong>⚡ Speed:</strong> {reportStats.avgGenerationTime || 'N/A'}
                        </div>
                      </div>
                    )}
                    
                    {isLoading && (
                      <div style={{ 
                        background: '#f8f9fa', 
                        padding: '1rem', 
                        borderRadius: '8px', 
                        marginBottom: '1rem',
                        fontSize: '0.85rem',
                        textAlign: 'center'
                      }}>
                        Loading report statistics...
                      </div>
                    )}
                    
                    <ReportFooter>
                      <Button as={Link} to={`/reports/${report.type.toLowerCase().replace(/\s+/g, '-')}`}>
                        Generate Report
                      </Button>
                    </ReportFooter>
                  </ReportCardBody>
                </ReportCard>
              );
            })}
          </Grid>
        </CardBody>
      </Card>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default ReportsList; 